import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { convertBirthToFullYear } from "../../../libs/dateFormat";
import { getQualtricsList } from "../../../libs/qualtrics";

import { checkCiDuplicate, join } from "../../../services/authServices";
import { PublicTermPurpose } from "../../../services/etcServices";

import useEmailVerification from "../../../hooks/useEmailVerification";
import useReferrerEmailVerification from "../../../hooks/useReferrerEmailVerification";
import useAgreement from "../../../hooks/useAgreement";
import useIdentityVerification from "../../../hooks/useIdentityVerification";
import usePasswordConfirm from "../../../hooks/usePasswordConfirm";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useNickName from "../../../hooks/useNickName";
import useSimplePopup from "../../../hooks/useSimplePopup";
import useShowQualtrics from "../../../hooks/useShowQualtrics";

import CommonButton from "../../../components/Button";
import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonPopup from "../../../components/Popup";
import ProgressBar from "../../../components/ProgressBar";
import TermsPopup from "../../../components/Popup/Terms";
import BottomGroup from "../../../components/Button/Group";
import { AuthHeader } from "../../../components/Layout/Auth/Header";

import emailDupConfirm from "../../../assets/img/email-dup-confirm.png";

type Props = {};

// eslint-disable-next-line no-empty-pattern
export default function SignUp(props: Props) {
  const SIGN_UP_STEP = {
    SANDI_AGREEMENT: 1,
    IDENTITY_VERIFICATION: 2,
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [popup, setPopup] = useState<string>("");
  const [
    {
      data: identityData,
      isComplete: isCompleteIdentity,
      validate: validateIdentity,
    },
    identityComponent,
  ] = useIdentityVerification({});
  const [
    { data: emailData, isComplete: isCompleteEmail, validate: validateEmail },
    emailVerificationComponent,
  ] = useEmailVerification({});
  const [
    {
      data: referrerEmailData,
      isCompleted: isCompletedReferrerEmail,
      validate: validateReferrerEmail,
    },
    referrerEmailVerificationComponent,
  ] = useReferrerEmailVerification({});
  const [currentStep, setCurrentStep] = useState<number>(
    SIGN_UP_STEP.SANDI_AGREEMENT
  );
  const [terms, setTerms] = useState<PublicTermPurpose | "">("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [showQualtrics] = useShowQualtrics({ open });
  const [
    {
      agreements: sandiData,
      isComplete: isCompleteAgreement,
      validate: validateAgreement,
    },
    sandiAgreementComponent,
  ] = useAgreement({
    allTitle: "전체동의",
    agreementList: [
      {
        title: "개인정보 수집 및 이용 동의",
        required: true,
        onClick: () => {
          setPopup("showTerms");
          setTerms("personal_gather");
          return true;
        },
      },
      {
        title: "서비스 이용약관 동의",
        required: true,
        onClick: () => {
          setPopup("showTerms");
          setTerms("service");
          return true;
        },
      },
      {
        title: "위치기반 이용약관 동의",
        required: true,
        onClick: () => {
          setPopup("showTerms");
          setTerms("location");
          return true;
        },
      },
      {
        title: "마케팅 처리 방침",
        required: false,
        onClick: () => {
          setPopup("showTerms");
          setTerms("marketing");
          return true;
        },
      },
      {
        title: "혜택/이벤트 알림(PUSH)",
        required: false,
      },
    ],
  });

  const [
    {
      data: passwordConfirmData,
      isComplete: isCompletePassword,
      validate: validatePassword,
    },
    passwordConfirmComponent,
  ] = usePasswordConfirm({});
  const [
    {
      data: nicknameData,
      isComplete: isCompleteNickname,
      validate: validateNickname,
    },
    nicknameConfirmComponent,
  ] = useNickName({
    placeholder: "별명을 입력해주세요",
    subLabel: "(앱 내 커뮤니티(샌디톡) 이용시 활용됩니다)",
  });

  const isDisabled = () => {
    if (currentStep === SIGN_UP_STEP.SANDI_AGREEMENT) {
      return !(
        isCompleteNickname &&
        isCompleteEmail &&
        isCompletedReferrerEmail &&
        isCompletePassword &&
        isCompleteAgreement
      );
    } else if (currentStep === SIGN_UP_STEP.IDENTITY_VERIFICATION) {
      return !isCompleteIdentity;
    }
  };

  const showQualtricsList = (popup: string, memberInfo: any) => {
    if (!popup) return;
    if (!location.pathname) return;

    const qualtricsList = getQualtricsList()[`${location.pathname}:${popup}`];
    if (!qualtricsList || qualtricsList.length === 0) return;

    showQualtrics(qualtricsList.join(","), memberInfo);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  return (
    <div className="px-5 pb-20">
      {loadingComponent}
      <StickyHeader
        title="샌디 아이디 만들기"
        onBackClick={() => {
          if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
          } else {
            navigate("/auth/login");
          }
        }}
      />
      <ProgressBar
        currentStep={currentStep}
        wrapperClassName="z-20"
        totalStep={Object.keys(SIGN_UP_STEP).length}
      />

      {currentStep === SIGN_UP_STEP.SANDI_AGREEMENT && (
        <>
          <div className="flex flex-col gap-5">
            <AuthHeader>
              <strong>샌디 회원가입</strong>을<br />
              도와드릴게요
            </AuthHeader>
            {nicknameConfirmComponent}
            {emailVerificationComponent}
            {referrerEmailVerificationComponent}
            {passwordConfirmComponent}
          </div>
          {sandiAgreementComponent}

          <BottomGroup>
            <CommonButton
              validation={isDisabled()}
              className="w-full h-bottom-button rounded-none"
              onClick={() => {
                if (
                  validateNickname() &&
                  validateEmail() &&
                  validateReferrerEmail() &&
                  validatePassword() &&
                  validateAgreement()
                ) {
                  setCurrentStep((prev) => prev + 1);
                }
              }}
            >
              동의 완료
            </CommonButton>
          </BottomGroup>
        </>
      )}

      {currentStep === SIGN_UP_STEP.IDENTITY_VERIFICATION && (
        <>
          {identityComponent}
          <BottomGroup>
            <CommonButton
              validation={isDisabled()}
              className="w-full h-bottom-button rounded-none"
              onClick={async () => {
                if (!validateIdentity()) return;
                setIsLoading(true);

                try {
                  const res = await checkCiDuplicate(identityData.ci as string);
                  if (
                    res.status === HttpStatusCode.Ok &&
                    res.data.isDuplicated === false
                  ) {
                    // nothing to do
                  } else {
                    open("이미 가입된 회원입니다.");
                    setIsLoading(false);
                    return;
                  }
                } catch (e: any) {
                  open("이미 가입된 회원입니다.");
                  setIsLoading(false);
                  return;
                }

                join({
                  name: identityData.name?.trim() as string,
                  nickname: nicknameData.nickname.trim() as string,
                  email: emailData.email.trim() as string,
                  referrerEmail: referrerEmailData.email.trim() as string,
                  password: passwordConfirmData.password as string,
                  mobileNumber: identityData.mdn as string,
                  memberCi: identityData.ci as string,
                  marketing: sandiData[3],
                  advertise: sandiData[4],
                  gender: Number(identityData.gender) % 2 === 1 ? "M" : "F",
                  birthday: convertBirthToFullYear(
                    identityData.birth as string
                  ),
                })
                  .then((res) => {
                    if (res.status === HttpStatusCode.Ok) {
                      setPopup("signUpComplete");
                      showQualtricsList("signUpComplete", res.data);
                    }
                  })
                  .catch((e: any) => {
                    if (e?.response?.data?.error) {
                      open(e?.response?.data?.error.replace(". ", ".\n"));
                    } else {
                      open(
                        "회원가입에 실패했습니다.\n관리자에게 문의해주세요."
                      );
                    }
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
            >
              회원가입
            </CommonButton>
          </BottomGroup>
        </>
      )}

      <CommonPopup
        isOpen={popup === "signUpComplete"}
        title="회원 가입 완료"
        padding={0}
      >
        <div className="px-5 pb-5">
          <div className="py-2.5 flex flex-col gap-2.5 items-center">
            <img src={emailDupConfirm} alt="dup-confirm" className="h-[60px]" />
            <div className=" whitespace-pre-line text-center text-brand-neutral-60 text-sm">
              {"회원가입이 완료되었습니다."}
            </div>
          </div>
        </div>

        <CommonButton
          className="h-bottom-button w-full rounded-none"
          onClick={() => navigate("/auth/login")}
        >
          로그인 페이지로 이동
        </CommonButton>
      </CommonPopup>

      <TermsPopup
        popup={popup}
        onClose={() => setPopup("")}
        type={terms as PublicTermPurpose}
      />

      {simplePopupComponent}
    </div>
  );
}
